import { isDevMode } from '@angular/core'
import { CompleteHocuspocusProviderConfiguration, ConstructableOutgoingMessage, HocuspocusProvider } from '@hocuspocus/provider'
import { createDecoder, readVarString, readVarUint } from 'lib0/decoding'
import { createEncoder, toUint8Array, writeVarString, writeVarUint, writeVarUint8Array } from 'lib0/encoding'
import { encodeStateVector } from 'yjs'

const MESSAGE_TYPE_STATELESS = 5

const MESSAGE_TYPE_NAMES: Record<number, string> = {
  0: 'Sync',
  1: 'Awareness',
  2: 'Auth',
  3: 'QueryAwareness',
  4: 'SyncReply',
  [MESSAGE_TYPE_STATELESS]: 'Stateless',
  6: 'BroadcastStateless',
  7: 'CLOSE',
  8: 'SyncStatus',
}

function describeMessage(bytes: Uint8Array): string {
  try {
    const decoder = createDecoder(bytes)
    const documentName = readVarString(decoder)
    const messageType = readVarUint(decoder)
    const typeName = MESSAGE_TYPE_NAMES[messageType] ?? `Unknown(${messageType})`
    return `type=${typeName}, document=${documentName}, size=${bytes.byteLength}B`
  } catch {
    return `size=${bytes.byteLength}B (failed to decode)`
  }
}

class SseSyncProviderLogger {
  private enabled = isDevMode()

  debug(message: string) {
    if (this.enabled) {
      console.debug(`[SseSyncProvider] ${message}`)
    }
  }

  warn(message: string) {
    console.warn(`[SseSyncProvider] ${message}`)
  }

  error(message: string) {
    console.error(`[SseSyncProvider] ${message}`)
  }

  messageReceived(bytes: Uint8Array) {
    if (this.enabled) {
      console.debug(`[SseSyncProvider] Message received: ${describeMessage(bytes)}`)
    }
  }
}

const logger = new SseSyncProviderLogger()

function toUrlSafeBase64(value: string): string {
  return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function bytesToBase64(bytes: Uint8Array): string {
  const chunkSize = 8192
  const parts: string[] = []
  for (let offset = 0; offset < bytes.byteLength; offset += chunkSize) {
    const chunk = bytes.subarray(offset, Math.min(offset + chunkSize, bytes.byteLength))
    parts.push(String.fromCharCode(...chunk))
  }
  return btoa(parts.join(''))
}

function base64ToBytes(base64: string): Uint8Array | null {
  try {
    const binary = atob(base64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i)
    }
    return bytes
  } catch {
    return null
  }
}

const FLUSH_INTERVAL_MS = 150

function encodeOutgoingMessage(Message: ConstructableOutgoingMessage, args: any = {}): Uint8Array {
  const message = new (Message as any)()
  return toUint8Array(message.get(args))
}

const STATELESS_SYNC_COMPLETE = 'sync-complete'

function sendMessages(sessionId: string, encodedMessages: string[]): Promise<Response> {
  return fetch(`/api/tiptap-editor/session/${sessionId}/message`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages: encodedMessages }),
    credentials: 'include',
  })
}

function postHeartbeatPong(sessionId: string, payload: string): Promise<Response> {
  return fetch(`/api/tiptap-editor/session/${sessionId}/heartbeat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ payload }),
    credentials: 'include',
  })
}

type BufferedOutboundMessage = {
  message: ConstructableOutgoingMessage
  args: any
}

export type SseSyncProviderConfiguration = Pick<CompleteHocuspocusProviderConfiguration, 'name' | 'document' | 'onSynced'> & {
  onStatelessPayload?: (payload: string) => void
}

const HEARTBEAT_TIMEOUT_MS = 15_000
const HEARTBEAT_CHECK_INTERVAL_MS = 5_000

function isRetryableStatus(status: number): boolean {
  return status === 429 || status >= 500
}

export class SseSyncProvider extends HocuspocusProvider {
  private eventSource: EventSource | null = null

  private connectionId: string | null = null

  private isDestroyed = false

  private isReconnecting = false
  /**
   * Used to buffer messages that get sent before we establish a connection with
   * the backend so we don't lose any messages.
   */
  private outboundBuffer: Array<BufferedOutboundMessage> = []
  /**
   * Used to buffer messages periodically so we can batch multiple messages
   * within the `FLUSH_INTERVAL_MS` period for sending in a single request.
   */
  private pendingMessages: string[] = []

  private flushTimer: ReturnType<typeof setTimeout> | null = null

  private lastHeartbeatTimestamp = 0

  private heartbeatCheckInterval: ReturnType<typeof setInterval> | null = null

  private ackedConnectionId: string | null = null

  constructor(configuration: SseSyncProviderConfiguration) {
    super({
      ...configuration,
      // Proxy all calls to the websocket provider as we're not using it for
      // syncing, but the HocuspocusProvider expects it to be there and will at
      // times make calls to it that we don't need. Passing a proxy allows us to
      // avoid having to rewrite the underlying provider whilst ensuring that
      // any calls to the websocket provider are effectively no-ops.
      websocketProvider: new Proxy({} as any, {
        get: () => () => {},
      }),
    })

    this.on('synced', () => this.acknowledgeInitialSync())
    this.connectSse()
  }

  private acknowledgeInitialSync(): void {
    if (this.isDestroyed || !this.synced || this.connectionId === null || this.connectionId === this.ackedConnectionId) {
      return
    }
    this.ackedConnectionId = this.connectionId
    this.sendStateless(STATELESS_SYNC_COMPLETE)
  }

  private connectSse(): void {
    if (this.eventSource !== null) {
      this.eventSource.close()
    }

    this.eventSource = new EventSource(`/api/tiptap-editor/document/${toUrlSafeBase64(this.configuration.name)}/sse`, {
      withCredentials: true,
    })

    this.eventSource.addEventListener('connectionId', (event: MessageEvent<string>) => {
      if (this.isDestroyed) {
        return
      }
      this.connectionId = event.data
      this.isReconnecting = false
      this.lastHeartbeatTimestamp = Date.now()
      logger.debug(`Connection established (sessionId: ${event.data})`)
      this.startHeartbeatMonitoring()
      this.enqueueSyncStep1()
      this.flushOutboundBuffer()
      this.acknowledgeInitialSync()
    })

    this.eventSource.addEventListener('message', (event: MessageEvent<string>) => {
      if (this.isDestroyed) {
        return
      }
      const bytes = base64ToBytes(event.data)
      if (bytes === null) {
        logger.error(`Discarding SSE message with invalid base64 payload (length=${event.data.length}).`)
        return
      }
      logger.messageReceived(bytes)
      this.handlePossibleStatelessMessage(bytes)
      this.onMessage({
        ...event,
        data: bytes.buffer as ArrayBuffer,
        target: null as any,
      })
    })

    this.eventSource.addEventListener('heartbeat', (event: MessageEvent<string>) => {
      logger.debug('Message received: type=Heartbeat')
      this.lastHeartbeatTimestamp = Date.now()
      this.sendHeartbeatPong(event.data)
    })

    this.eventSource.addEventListener('error', () => {
      if (this.isDestroyed) {
        return
      }
      this.reconnectSse()
    })
  }

  private reconnectSse(): void {
    if (this.isDestroyed || this.isReconnecting) {
      return
    }

    logger.debug('Disconnecting from EventSource, will reconnect')
    this.isReconnecting = true
    this.connectionId = null
    this.clearFlushTimer()
    this.stopHeartbeatMonitoring()
    this.connectSse()
  }

  override destroy(): void {
    logger.debug('Disconnecting from EventSource, provider destroyed')
    this.isDestroyed = true
    this.outboundBuffer = []
    this.pendingMessages = []
    this.clearFlushTimer()
    this.stopHeartbeatMonitoring()
    if (this.eventSource !== null) {
      this.eventSource.close()
      this.eventSource = null
    }
    super.destroy()
  }

  override attach() {
    // no-op: Use SSE instead.
  }

  override detach(): void {
    // no-op: Use SSE instead.
  }

  override async send(message: ConstructableOutgoingMessage, args: any): Promise<void> {
    if (this.isDestroyed) {
      return
    }

    if (this.connectionId === null || this.isReconnecting) {
      this.outboundBuffer.push({ message, args })
      return
    }

    this.pendingMessages.push(bytesToBase64(encodeOutgoingMessage(message, args)))
    this.scheduleFlush()
  }

  private sendHeartbeatPong(payload: string): void {
    if (this.isDestroyed || this.connectionId === null || payload.length === 0) {
      return
    }
    void postHeartbeatPong(this.connectionId, payload).catch(() => {})
  }

  private startHeartbeatMonitoring() {
    this.stopHeartbeatMonitoring()
    this.heartbeatCheckInterval = setInterval(() => {
      if (Date.now() - this.lastHeartbeatTimestamp > HEARTBEAT_TIMEOUT_MS) {
        logger.warn('Heartbeat timeout, reconnecting SSE')
        this.reconnectSse()
      }
    }, HEARTBEAT_CHECK_INTERVAL_MS)
  }

  private stopHeartbeatMonitoring() {
    if (this.heartbeatCheckInterval !== null) {
      clearInterval(this.heartbeatCheckInterval)
      this.heartbeatCheckInterval = null
    }
  }

  private clearFlushTimer(): void {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer)
      this.flushTimer = null
    }
  }

  private scheduleFlush(): void {
    if (this.flushTimer !== null) {
      return
    }

    this.flushTimer = setTimeout(() => {
      this.flushTimer = null
      this.flushPendingMessages()
    }, FLUSH_INTERVAL_MS)
  }

  private requeueAndReconnect(failedMessages: string[]): void {
    this.pendingMessages.unshift(...failedMessages)
    this.reconnectSse()
  }

  private async flushPendingMessages(): Promise<void> {
    if (this.isDestroyed || this.connectionId === null || this.pendingMessages.length === 0) {
      return
    }

    const messages = this.pendingMessages
    this.pendingMessages = []

    let response: Response
    try {
      response = await sendMessages(this.connectionId, messages)
    } catch {
      this.requeueAndReconnect(messages)
      return
    }

    if (!response.ok && isRetryableStatus(response.status)) {
      this.requeueAndReconnect(messages)
    }
  }

  private flushOutboundBuffer(): void {
    const messages = this.outboundBuffer
    this.outboundBuffer = []

    for (const bufferedMessage of messages) {
      this.send(bufferedMessage.message, bufferedMessage.args)
    }
  }

  private handlePossibleStatelessMessage(bytes: Uint8Array): void {
    const onStatelessPayload = (this.configuration as SseSyncProviderConfiguration).onStatelessPayload
    if (onStatelessPayload === undefined) {
      return
    }

    try {
      const decoder = createDecoder(bytes)
      readVarString(decoder) // skip document name
      const messageType = readVarUint(decoder)
      if (messageType === MESSAGE_TYPE_STATELESS) {
        const payload = readVarString(decoder)
        logger.debug(`Stateless message received: payload=${payload}`)
        onStatelessPayload(payload)
      }
    } catch (error) {
      logger.warn(`Failed to parse possible stateless message: ${error}`)
    }
  }

  private enqueueSyncStep1(): void {
    if (this.isDestroyed || this.connectionId === null) {
      return
    }

    const encoder = createEncoder()
    writeVarString(encoder, this.configuration.name)
    writeVarUint(encoder, 0) // MessageType.Sync
    writeVarUint(encoder, 0) // messageYjsSyncStep1
    writeVarUint8Array(encoder, encodeStateVector(this.document))

    this.pendingMessages.push(bytesToBase64(toUint8Array(encoder)))
    this.scheduleFlush()
  }
}
